import React, { useState } from 'react';
import ConversationCard from '../ConversationCard/ConversationCard';
import ConversationModal from '../ConversationModal/ConversationModal';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { FaInbox, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import './ConversationLanes.scss';

const ConversationLanes = ({
    laneData,
    activeStatusFilter,
    onLanePageChange,
    onConversationUpdate,
    onCreateReminderFlow,
    creatingReminderFlowConversationId,
    onMessageSent,
    technician,
    isAdmin,
    isManager
}) => {
    const [draggedCard, setDraggedCard] = useState(null);
    const [selectedConversation, setSelectedConversation] = useState(null);

    const lanes = [
        {
            id: 'pending',
            title: 'Pending',
            icon: FaInbox,
            color: '#6c757d',
            description: 'Awaiting customer response'
        },
        {
            id: 'confirmed',
            title: 'Confirmed',
            icon: FaCheckCircle,
            color: '#28a745',
            description: 'Appointment confirmed'
        },
        {
            id: 'needs_reschedule',
            title: 'Needs Reschedule',
            icon: FaExclamationTriangle,
            color: '#ffc107',
            description: 'Customer wants to reschedule'
        }
    ];

    const visibleLanes = activeStatusFilter === 'all'
        ? lanes
        : lanes.filter(lane => lane.id === activeStatusFilter);

    const handleDragStart = (e, conversation) => {
        setDraggedCard(conversation);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();

        if (draggedCard && draggedCard.status !== newStatus) {
            await onConversationUpdate(draggedCard.id, { status: newStatus });
        }

        setDraggedCard(null);
    };

    const handleDragEnd = () => {
        setDraggedCard(null);
    };

    const handleQuickAction = (conversationId, action) => {
        switch (action) {
            case 'confirm':
                onConversationUpdate(conversationId, { status: 'confirmed' });
                break;
            case 'reschedule':
                onConversationUpdate(conversationId, { status: 'needs_reschedule' });
                break;
            default:
                break;
        }
    };

    const handleViewConversation = (conversation) => {
        setSelectedConversation(conversation);
    };

    const handleCloseModal = () => {
        setSelectedConversation(null);
    };

    const handleSendMessage = async (conversationId) => {
        console.log('Message sent for conversation:', conversationId);
        if (onMessageSent) {
            await onMessageSent(false);
        }
    };

    const handleMessagesRead = async (conversationId) => {
        console.log('Messages read for conversation:', conversationId);
        if (onMessageSent) {
            await onMessageSent(false);
        }
    };

    return (
        <div className="conversation-lanes">
            {visibleLanes.map(lane => {
                const laneState = laneData[lane.id] || {
                    conversations: [],
                    page: 1,
                    totalPages: 1,
                    totalCount: 0,
                    loading: false
                };
                const laneConversations = laneState.conversations || [];
                const IconComponent = lane.icon;
                const pageStart = laneState.totalCount === 0 ? 0 : ((laneState.page - 1) * laneState.limit) + 1;
                const pageEnd = laneState.totalCount === 0
                    ? 0
                    : Math.min(laneState.page * laneState.limit, laneState.totalCount);

                return (
                    <div
                        key={lane.id}
                        className={`conversation-lane ${draggedCard ? 'conversation-lane--dragging' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, lane.id)}
                    >
                        <div className="conversation-lane__header">
                            <div className="lane-title">
                                <IconComponent className="lane-icon" />
                                <div>
                                    <h3>{lane.title}</h3>
                                    <span className="lane-count">
                                        {laneState.totalCount} conversation{laneState.totalCount !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                            <div className="lane-description">
                                {lane.description}
                            </div>
                        </div>

                        <div className="conversation-lane__content">
                            {laneState.loading ? (
                                <div className="conversation-lane__loading">
                                    <LoadingSpinner size="small" />
                                </div>
                            ) : laneConversations.length === 0 ? (
                                <div className="conversation-lane__empty">
                                    <IconComponent className="empty-icon" />
                                    <p>No conversations in this lane</p>
                                </div>
                            ) : (
                                laneConversations.map(conversation => (
                                    <ConversationCard
                                        key={conversation.id}
                                        conversation={conversation}
                                        onDragStart={handleDragStart}
                                        onDragEnd={handleDragEnd}
                                        onQuickAction={handleQuickAction}
                                        onCreateReminderFlow={onCreateReminderFlow}
                                        onViewConversation={handleViewConversation}
                                        isDragging={draggedCard?.id === conversation.id}
                                        isCreatingReminderFlow={creatingReminderFlowConversationId === conversation.id}
                                        technician={technician}
                                        canEdit={isAdmin || isManager || conversation.technicianId === technician.id}
                                    />
                                ))
                            )}
                        </div>

                        <div className="conversation-lane__pagination">
                            <span className="lane-page-summary">
                                Showing {pageStart}-{pageEnd} of {laneState.totalCount}
                            </span>

                            <div className="lane-page-controls">
                                <button
                                    className="lane-page-button"
                                    onClick={() => onLanePageChange(lane.id, laneState.page - 1)}
                                    disabled={laneState.loading || laneState.page <= 1}
                                >
                                    Previous
                                </button>

                                <span className="lane-page-indicator">
                                    Page {laneState.page} of {laneState.totalPages}
                                </span>

                                <button
                                    className="lane-page-button"
                                    onClick={() => onLanePageChange(lane.id, laneState.page + 1)}
                                    disabled={laneState.loading || laneState.page >= laneState.totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}

            {selectedConversation && (
                <ConversationModal
                    conversation={selectedConversation}
                    onClose={handleCloseModal}
                    onSendMessage={handleSendMessage}
                    onMessagesRead={handleMessagesRead}
                />
            )}
        </div>
    );
};

export default ConversationLanes;
